// ABOUTME: Shared detail panel for the JF PDP feature cards (ingredient, CTA, product-feature blocks).
// Opens a full-screen modal on mobile (<=768px) and a 500px right slide-out on desktop; cards opt in
// with [data-jf-feature-card] plus data-modal-title / data-modal-image-position attributes and an inert
// <template data-jf-feature-panel> holding the panel image and rich text, rendered by Liquid and cloned
// into the panel as nodes (no markup string is parsed). Loaded as a module so repeated tags execute once.
// Both panels are named modal dialogs with a full focus lifecycle: focus moves to the close button on
// open, Tab and Shift+Tab stay inside the open panel, the closed panels are inert (out of the tab order
// and the accessibility tree), and closing returns focus to the card that opened the panel.

const STYLE = `
  .jf-feature-modal,
  .jf-feature-slideout {
    position: fixed;
    top: 0;
    background: #ffffff;
    z-index: 200;
    overflow-y: auto;
    transition: transform 0.3s ease;
  }

  .jf-feature-modal {
    left: 0;
    width: 100%;
    height: 100%;
    /* Full-viewport panel (viewport-fit=cover): keep content clear of the notch and home indicator */
    box-sizing: border-box;
    padding: var(--safe-area-inset-top, 0px) var(--safe-area-inset-right, 0px) var(--safe-area-inset-bottom, 0px) var(--safe-area-inset-left, 0px);
    transform: translateX(100%);
  }

  .jf-feature-modal.active {
    transform: translateX(0);
  }

  .jf-feature-slideout {
    right: 0;
    width: 500px;
    height: 100%;
    /* Right-anchored panel (viewport-fit=cover): clear of the status bar, right notch and home indicator */
    box-sizing: border-box;
    padding: var(--safe-area-inset-top, 0px) var(--safe-area-inset-right, 0px) var(--safe-area-inset-bottom, 0px) 0;
    transform: translateX(100%);
  }

  .jf-feature-slideout.active {
    transform: translateX(0);
  }

  .jf-feature-close {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 16px;
    line-height: 125%;
    font-family: 'OCR B Std', sans-serif;
    background: none;
    border: none;
    cursor: pointer;
    z-index: 10;
  }

  .jf-feature-content {
    padding: 24px;
    margin-top: 48px;
  }

  .jf-feature-content h3 {
    font-family: 'Reload Medium', sans-serif;
    font-weight: 400;
    color: #393536;
    font-size: 32px;
    line-height: 102%;
    margin: 0;
  }

  .jf-feature-content .jf-feature-image {
    width: 100%;
    height: auto;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .jf-feature-text {
    font-family: Ciutadella, sans-serif;
    font-size: 18px;
    line-height: 133%;
    margin-top: 16px;
  }

  .jf-feature-text p {
    margin: 0 0 1em;
  }

  .jf-feature-text p:last-child {
    margin-bottom: 0;
  }

  .jf-feature-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  .jf-feature-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 199;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease;
  }

  .jf-feature-mask.active {
    opacity: 1;
    visibility: visible;
  }
`;

function isMobile() {
  return window.innerWidth <= 768;
}

function buildPanels() {
  const style = document.createElement('style');
  style.textContent = STYLE;
  document.head.appendChild(style);

  const mask = document.createElement('div');
  mask.className = 'jf-feature-mask';
  mask.setAttribute('data-jf-feature-mask', '');

  const closeButton = `
    <button type="button" class="jf-feature-close" data-jf-feature-close aria-label="Close panel">
      <span>CLOSE</span>
      &times;
    </button>
  `;

  const modal = document.createElement('div');
  modal.className = 'jf-feature-modal';
  modal.setAttribute('data-jf-feature-modal', '');
  modal.innerHTML = `
    ${closeButton}
    <div class="jf-feature-content">
      <div data-jf-feature-body></div>
    </div>
  `;

  const slideout = document.createElement('div');
  slideout.className = 'jf-feature-slideout';
  slideout.setAttribute('data-jf-feature-slideout', '');
  slideout.innerHTML = `
    ${closeButton}
    <div class="jf-feature-content" data-jf-feature-body></div>
  `;

  for (const [panel, name] of [[modal, 'modal'], [slideout, 'slideout']]) {
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', `jf-feature-title-${name}`);
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
  }

  document.body.append(mask, modal, slideout);
  return { mask, modal, slideout };
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function init() {
  if (window.__jfFeatureSlideout) return;
  window.__jfFeatureSlideout = true;

  const { mask, modal, slideout } = buildPanels();
  /** The element that opened the current panel; focus returns to it on close. */
  let opener = null;

  function activePanel() {
    return [modal, slideout].find((panel) => panel.classList.contains('active')) || null;
  }

  /**
   * @param {DocumentFragment} content The card's panel template content: an optional
   *   [data-jf-feature-image] followed by the rich-text body.
   */
  function open(content, title, imagePosition) {
    const mobile = isMobile();
    const panel = mobile ? modal : slideout;
    const body = panel.querySelector('[data-jf-feature-body]');
    const heading = document.createElement('h3');
    heading.setAttribute('data-jf-feature-title', '');
    heading.textContent = title;
    heading.id = panel.getAttribute('aria-labelledby');
    body.replaceChildren(heading, content);

    // The template's image lands after the heading; "above" moves it ahead of the heading.
    const image = body.querySelector('[data-jf-feature-image]');
    if (image && imagePosition === 'above') body.prepend(image);

    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.inert = false;
    panel.classList.add('active');
    panel.setAttribute('aria-hidden', 'false');
    mask.classList.toggle('active', !mobile);
    panel.querySelector('[data-jf-feature-close]').focus();
  }

  function close() {
    const wasOpen = activePanel();
    for (const panel of [modal, slideout]) {
      panel.classList.remove('active');
      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
    }
    mask.classList.remove('active');
    if (wasOpen && opener?.isConnected) opener.focus();
    opener = null;
  }

  // Tab and Shift+Tab cycle inside the open panel: the page behind it is inert to the keyboard
  // while the dialog is modal.
  function containFocus(event) {
    const panel = activePanel();
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = panel.contains(document.activeElement);
    if (event.shiftKey) {
      if (!inside || document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!inside || document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openFromCard(card) {
    const template = card.querySelector('template[data-jf-feature-panel]');
    const content = template ? template.content.cloneNode(true) : document.createDocumentFragment();
    // A cloned template runs the scripts it carries once inserted; the panel body is display
    // markup only, so none of them are kept.
    for (const script of content.querySelectorAll('script')) script.remove();
    open(
      content,
      card.getAttribute('data-modal-title') || '',
      card.getAttribute('data-modal-image-position') || 'above'
    );
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-jf-feature-close]')) {
      close();
      return;
    }
    const card = event.target.closest('[data-jf-feature-card]');
    if (card) openFromCard(card);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (activePanel()) {
        event.preventDefault();
        close();
      }
      return;
    }
    if (event.key === 'Tab') {
      containFocus(event);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      // The card itself is the focusable control, so a key pressed on something nested inside it
      // belongs to that control.
      const card = event.target instanceof Element && event.target.matches('[data-jf-feature-card]')
        ? event.target
        : null;
      if (card) {
        event.preventDefault();
        openFromCard(card);
      }
    }
  });

  mask.addEventListener('click', close);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
}

init();
